// Geocoding API tab — extracted from the legacy SecurityDashboard (wave 3:
// Dashboard tiles, the modern table, the date picked in Addis Ababa time).
//
// One-time fetch of /google_api_stats (30-day window) plus one fetch
// of /google_api_logs/{selectedDate} (limitToLast 200) on date change. Both
// deferred until the admin opens the tab (unchanged). The stats and logs are
// stored under 'YYYY-MM-DD' day keys; the picker shows that same calendar day.

import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get, query, limitToLast } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DashboardCard from '../../components/DashboardCard';
import CircularLoading from '../../components/CircularLoading';
import { InlineMessage, EmptyState } from '../../components/feedback';
import { DataTable, SectionCard, FieldLabel, DropChip, AddisDateTimeField, msToAddisParts, addisPartsToMs, formatNumber } from '../../components/modern';
import { formatAddisDate } from '../../common/exportUtils';
import { tokens as c } from '../../theme';

const pad2 = (n) => (n < 10 ? `0${n}` : String(n));
// 'YYYY-MM-DD' day key <-> the instant the picker shows for that calendar day.
const keyToMs = (key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  return m ? addisPartsToMs({ y: +m[1], m: +m[2], d: +m[3], h: 0, mi: 0 }) : null;
};
const msToKey = (ms) => {
  const p = msToAddisParts(ms);
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`;
};
const keyLabel = (key) => {
  const ms = keyToMs(key);
  return ms === null ? key : formatAddisDate(ms, { weekday: true });
};

export default function GeocodingApi() {
  const { t } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [geocodingStats, setGeocodingStats] = useState({});
  const [geocodingLogs, setGeocodingLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedApiType, setSelectedApiType] = useState('all');
  const [statsLoading, setStatsLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => {
    if (!database || !isAdmin) return;
    let cancelled = false;
    const fetchStats = async () => {
      const snapshot = await get(ref(database, 'google_api_stats'));
      if (cancelled) return;
      setGeocodingStats(snapshot.val() || {});
      setStatsLoading(false);
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [database, isAdmin]);

  useEffect(() => {
    if (!database || !selectedDate || !isAdmin) return;
    let cancelled = false;
    const fetchApiLogs = async () => {
      setLogsLoading(true);
      const logsQuery = query(ref(database, `google_api_logs/${selectedDate}`), limitToLast(200));
      const snapshot = await get(logsQuery);
      const data = snapshot.val();
      if (cancelled) return;
      if (data) {
        const logsArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        logsArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setGeocodingLogs(logsArray);
      } else {
        setGeocodingLogs([]);
      }
      setLogsLoading(false);
    };
    fetchApiLogs();
    return () => {
      cancelled = true;
    };
  }, [database, selectedDate, isAdmin]);

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  if (statsLoading) return <CircularLoading inline />;

  const day = geocodingStats[selectedDate] || {};
  const apiTypes = [
    { value: 'all', label: t('geo_api_all_types', { defaultValue: 'All API Types' }) },
    { value: 'geocoding', label: 'Geocoding' },
    { value: 'places_autocomplete', label: 'Places Autocomplete' },
    { value: 'place_details', label: 'Place Details' },
    { value: 'directions', label: 'Directions' },
    { value: 'distance_matrix', label: 'Distance Matrix' },
  ];
  const logs = geocodingLogs.filter((log) => selectedApiType === 'all' || log.apiType === selectedApiType);
  const dates = Object.keys(geocodingStats).sort().reverse().slice(0, 30);
  const maxCalls = Math.max(1, ...Object.keys(geocodingStats).map((d) => geocodingStats[d]?.total || 0));

  const requestText = (log) => {
    const r = log.requestData || {};
    if (r.coordinates) return r.coordinates;
    if (r.searchKeyword) return r.searchKeyword;
    if (r.place_id) return r.place_id;
    if (r.start || r.dest) return `${r.start} → ${r.dest}`;
    return 'N/A';
  };
  const responseText = (log) => {
    const r = log.responseData || {};
    if (r.address) return r.address;
    if (r.resultCount !== undefined && r.resultCount !== null) return t('geo_api_results', { count: r.resultCount, defaultValue: '{{count}} results' });
    if (r.coords) return typeof r.coords === 'string' ? r.coords : JSON.stringify(r.coords);
    if (r.distance) return String(r.distance);
    return 'N/A';
  };
  const clip = (text) => (
    <Box component="span" title={text} sx={{ display: 'inline-block', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom', fontSize: 13 }}>
      {text}
    </Box>
  );

  const columns = [
    { title: t('time', { defaultValue: 'Time' }), field: 'timestamp', type: 'time', defaultSort: 'desc' },
    { title: t('geo_api_type', { defaultValue: 'API Type' }), field: 'apiType', render: (log) => log.apiType || 'unknown' },
    { id: 'request', title: t('geo_api_request', { defaultValue: 'Request' }), searchValue: requestText, exportValue: requestText, render: (log) => clip(requestText(log)) },
    { id: 'response', title: t('geo_api_response', { defaultValue: 'Response' }), searchValue: responseText, exportValue: responseText, render: (log) => clip(responseText(log)) },
    { title: t('user_type', { defaultValue: 'User Type' }), field: 'userType', render: (log) => log.userType || 'unknown' },
    {
      title: t('status', { defaultValue: 'Status' }),
      field: 'success',
      type: 'status',
      sortValue: (log) => (log.success ? 1 : 0),
      exportValue: (log) => (log.success ? 'SUCCESS' : 'FAILED'),
      status: (log) =>
        log.success
          ? { tone: 'success', icon: <CheckCircleOutlineRoundedIcon />, label: t('success', { defaultValue: 'Success' }) }
          : { tone: 'error', icon: <ErrorOutlineRoundedIcon />, label: t('failed', { defaultValue: 'Failed' }) },
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineMessage kind="info">
        {t('geo_api_info', { defaultValue: 'All Google API usage is monitored for security and cost optimization. Logs are kept for 2 days, stats for 30 days.' })}
      </InlineMessage>

      {/* Stats of the chosen day */}
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
        <DashboardCard icon={<MapOutlinedIcon />} hue="info" title={t('geo_api_calls', { defaultValue: 'API calls' })} value={formatNumber(day.total || 0)} caption={keyLabel(selectedDate)} />
        <DashboardCard icon={<TrendingUpRoundedIcon />} hue="success" title="Geocoding" value={formatNumber(day.geocoding || 0)} />
        <DashboardCard icon={<SpeedOutlinedIcon />} hue="amber" title="Directions" value={formatNumber(day.directions || 0)} />
        <DashboardCard icon={<PlaceOutlinedIcon />} hue="accent" title="Places API" value={formatNumber((day.places_autocomplete || 0) + (day.place_details || 0))} />
      </Box>

      {/* Date and API type */}
      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <Box>
          <FieldLabel htmlFor="geo-date">{t('geo_api_select_date', { defaultValue: 'Select Date' })}</FieldLabel>
          <AddisDateTimeField
            id="geo-date"
            dateOnly
            value={keyToMs(selectedDate)}
            onChange={(ms) => {
              if (ms) setSelectedDate(msToKey(ms));
            }}
          />
        </Box>
        <DropChip label={t('geo_api_type', { defaultValue: 'API Type' })} value={selectedApiType} onChange={setSelectedApiType} options={apiTypes} />
      </Paper>

      {/* Last 30 days */}
      <SectionCard id="geo-days" icon={<TrendingUpRoundedIcon />} hue="info" title={t('geo_api_last_30', { defaultValue: 'Last 30 Days API Usage' })}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1.5 }}>
          {dates.length === 0 ? (
            <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
              {t('geo_api_no_stats', { defaultValue: 'No stats yet.' })}
            </Typography>
          ) : (
            dates.map((date) => {
              const s = geocodingStats[date] || {};
              const totalCalls = s.total || 0;
              const selected = date === selectedDate;
              return (
                <Box key={date} sx={{ display: 'grid', gridTemplateColumns: { xs: '110px minmax(0, 1fr) 56px', md: '140px minmax(0, 1fr) 64px 200px' }, alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: selected ? c.ACCENT_TEXT : c.INK_2, fontWeight: selected ? 700 : 400, whiteSpace: 'nowrap' }}>
                    {keyLabel(date)}
                  </Typography>
                  <Box sx={{ height: 14, bgcolor: c.SURFACE_ALT, borderRadius: '7px', overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.min((totalCalls / maxCalls) * 100, 100)}%`, bgcolor: selected ? c.ACCENT : c.OUTLINE_STRONG, borderRadius: '7px' }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(totalCalls)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: c.TEXT_3, display: { xs: 'none', md: 'block' }, fontVariantNumeric: 'tabular-nums' }}>
                    G:{s.geocoding || 0} D:{s.directions || 0} P:{(s.places_autocomplete || 0) + (s.place_details || 0)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </SectionCard>

      {/* Detailed logs */}
      <DataTable
        title={t('geo_api_logs_title', { date: keyLabel(selectedDate), defaultValue: 'Detailed Logs for {{date}}' })}
        rows={logs}
        columns={columns}
        rowKey="id"
        loading={logsLoading && !geocodingLogs.length}
        refreshing={logsLoading && geocodingLogs.length > 0}
        countText={({ shown, total, query, loading }) =>
          loading
            ? t('loading', { defaultValue: 'Loading…' })
            : `${keyLabel(selectedDate)} · ${
                query
                  ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
                  : t('geo_api_log_count', { count: total, defaultValue_one: '{{count}} call', defaultValue_other: '{{count}} calls', defaultValue: '{{count}} calls' })
              }`
        }
        searchPlaceholder={t('geo_api_search', { defaultValue: 'Search request or response' })}
        prefsKey="security_geocoding_logs"
        minWidth={900}
        emptyState={({ query }) => (query ? null : { icon: <MapOutlinedIcon />, title: t('geo_api_no_logs', { defaultValue: 'No API logs for this date/type' }) })}
      />
    </Box>
  );
}
